import { changeI18nLanguage, cleanArray, createEventData, pushToDataLayer, updateIntercomUser } from '@arnold/common';
import { getLocale } from '@arnold/core';
import { format } from 'date-fns';
import { DocumentNode, getOperationAST } from 'graphql';
import { TFunction } from 'react-i18next';
import {
  ContactSource,
  ContactType,
  OrganizationTeamsQuery,
  SurveyQuery,
  UpdateRespondentMutation,
} from '../generated/hooks';
import auth from './auth';
import storage from './storage';

export const allowedLanguages = Object.freeze(['en', 'cs', 'sk', 'hr']);

export const handleLanguageChange = (code: string) => {
  storage.setDetectedLanguage(code);
  changeI18nLanguage(code);
  updateIntercomUser({
    language_override: code,
    user_hash: auth.getIntercomHash(),
  });
};

export const getRawContact = (
  member:
    | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]
    | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['leader']
    | UpdateRespondentMutation['updateRespondent'],
  contactType: ContactType,
  contactSource?: ContactSource,
): NonNullable<NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]['contacts']>[0] | null => {
  if (member?.contacts != null) {
    const contact = member.contacts.find(
      (contact) => contact.type === contactType && (!contactSource || contact.source === contactSource),
    );
    if (contact) {
      return contact as NonNullable<
        NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]['contacts']
      >[0];
    }
  }
  return null;
};

export const getContact = (
  member:
    | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]
    | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['leader']
    | UpdateRespondentMutation['updateRespondent'],
  contactType: ContactType,
  contactSource?: ContactSource,
) => {
  const contact = getRawContact(member, contactType, contactSource);
  return contact ? contact.value : '';
};

type MemberOrLeader =
  | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]
  | NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['leader'];
type ParamsWithoutTeamName = [respondent: MemberOrLeader];
type ParamsWithTeamName = [respondent: MemberOrLeader, teamName: string, t: TFunction<'updateRespondent', undefined>];

export const getTopicGroupTranslationByLanguageId = <T extends { language: { id: string } }>(
  languageId: string,
  translations?: T[] | null,
) => translations?.find((t) => t.language.id === languageId);

export const getRespondentNameLabel = (...params: ParamsWithTeamName | ParamsWithoutTeamName) => {
  const [respondent, teamName, t] = params;
  let name = `${respondent?.surname} ${respondent?.firstname} (${
    getContact(respondent!, ContactType.Email, ContactSource.Primary) ||
    getContact(respondent!, ContactType.Email, ContactSource.Respondent) ||
    getContact(respondent!, ContactType.Sms, ContactSource.Respondent)
  })`;
  if (teamName) {
    name += `, ${t?.('team')}: ${teamName}`;
  }
  return name;
};

export const getOperationName = (document: DocumentNode) => {
  const result = getOperationAST(document);
  if (result && result.name) {
    return result.name.value;
  }
};

type Language = { code: string; id: string };
export const sortLanguages = (languages: Language[], languageTranslations: Map<string, string>): Language[] => {
  const priorLanguages = cleanArray([
    languages.find((l) => l.code === 'cs'),
    languages.find((l) => l.code === 'en'),
    languages.find((l) => l.code === 'sk'),
  ] as Language[]);

  const languagesSorted = languages.filter((l) => !['cs', 'en', 'sk'].includes(l.code));
  languagesSorted.sort((l1, l2) =>
    (languageTranslations.get(l1.code) || '').localeCompare(languageTranslations.get(l2.code) || ''),
  );

  return [...priorLanguages, ...languagesSorted];
};

export const EDITION = {
  FREE: {
    name: 'FREE',
    licenceCount: 15,
  },
  BUSINESS: {
    name: 'BUSINESS',
    licenceCount: 50,
  },
  CREDITS: {
    name: 'CREDITS',
    licenceCount: null,
  },
};

export const sortRespondents = (respondents: NonNullable<SurveyQuery['survey']>['whitelistedRespondents']) => {
  return respondents.sort((a, b) => {
    return (a.surname || '').localeCompare(b.surname || '');
  });
};

export const formatFullDate = (date: string, language: string, short: boolean = true): string =>
  format(new Date(date), short ? 'PPp' : 'PPPPp', { locale: getLocale(language) });

export const handleSummaryResultsClick = (userId: string, orgId: string) => {
  pushToDataLayer({
    userId,
    orgId,
    event: 'ux.open-summary-results-from-admin',
    ...createEventData('survey', 'openSummaryResultsFromAdmin', 'survey open summary results from admin'),
  });
};
